/**
 * Shared filter-matching rule for every searchable list in the ui/select,
 * ui/combobox family. This is the ONE sanctioned way to filter a list built
 * on these primitives — do not hand-roll a different comparison at a call
 * site.
 *
 * Case-insensitive, trimmed, and accent-insensitive (NFD-normalized,
 * diacritics stripped): "Heredia" finds "Herediano", "alajuelense" finds
 * "ALAJUELENSE FF", a typed "alajuelense " with a trailing space still
 * matches. This app is Spanish-facing (team/competition names routinely
 * carry accents — "Herediano", "Limón", "Cartaginés") and this is the one
 * place that rule is enforced, so every rollout call site behaves
 * identically instead of drifting into 14 slightly different search
 * behaviours (PMA-UI Phase 1 review finding).
 */

// Unicode "Combining Diacritical Marks" block (U+0300–U+036F) — what NFD
// normalization splits an accented character into (e.g. "é" -> "e" +
// U+0301). Filtered by code point rather than a regex literal containing
// raw combining glyphs, so the source stays unambiguous in any editor.
const COMBINING_DIACRITICS_START = 0x0300;
const COMBINING_DIACRITICS_END = 0x036f;

export function normalizeForMatch(value: string): string {
  const decomposed = value.trim().toLowerCase().normalize("NFD");
  let result = "";
  for (const char of decomposed) {
    const codePoint = char.codePointAt(0) ?? 0;
    if (
      codePoint >= COMBINING_DIACRITICS_START &&
      codePoint <= COMBINING_DIACRITICS_END
    ) {
      continue;
    }
    result += char;
  }
  return result;
}

/** True when `query` (after normalizing) is a substring of `candidate`
 *  (after normalizing). An empty/whitespace-only query always matches. */
export function matchesQuery(candidate: string, query: string): boolean {
  const normalizedQuery = normalizeForMatch(query);
  if (!normalizedQuery) return true;
  return normalizeForMatch(candidate).includes(normalizedQuery);
}
